/**
 * The contact details an online order needs — shared by the checkout page
 * (which tells the guest BEFORE they pay) and the API (which enforces it), so
 * the two can never disagree about what a valid address is.
 *
 * Why it lives here: a page that accepts an address the API then rejects
 * leaves a guest who has filled in every field staring at a failed Pay button.
 * `isCheckoutEmail` accepts EXACTLY the language of the API's `format: "email"`
 * (ajv-formats 3, "full" mode) — `apps/api/test/orders/checkoutEmailParity.test.ts`
 * holds them together against the real compiled route schema.
 *
 * Written as a scanner, not a regex: the obvious regexes for this are either
 * looser than the API's (`[^\s@]+@[^\s@]+\.[^\s@]+`, which is also polynomial —
 * see `isEmailShaped`) or backtracking-prone. This is linear.
 */

const LOCAL_CHARS = new Set("abcdefghijklmnopqrstuvwxyz0123456789!#$%&'*+/=?^_`{|}~-");

function isAlnum(c: string): boolean {
    return (c >= 'a' && c <= 'z') || (c >= '0' && c <= '9');
}

/** One domain label: `[a-z0-9]`, optionally `[a-z0-9-]*` then `[a-z0-9]`. */
function isDomainLabel(label: string): boolean {
    if (label.length === 0) return false;
    if (!isAlnum(label[0]) || !isAlnum(label[label.length - 1])) return false;
    for (const c of label) if (!isAlnum(c) && c !== '-') return false;
    return true;
}

/**
 * True when the API will accept `value` as `customerInfo.email`. Case-
 * insensitive and whitespace-sensitive (callers trim; the page sends the
 * trimmed value).
 */
export function isCheckoutEmail(value: string | null | undefined): boolean {
    if (!value) return false;
    const v = value.toLowerCase();
    const at = v.indexOf('@');
    if (at <= 0 || at !== v.lastIndexOf('@')) return false;

    // Local part: dot-separated, non-empty atoms of the allowed characters.
    const local = v.slice(0, at);
    for (const atom of local.split('.')) {
        if (atom.length === 0) return false;
        for (const c of atom) if (!LOCAL_CHARS.has(c)) return false;
    }

    // Domain: at least two labels (the API requires a dot).
    const labels = v.slice(at + 1).split('.');
    if (labels.length < 2) return false;
    return labels.every(isDomainLabel);
}

/**
 * A usable US mobile number: ten digits once formatting and a leading
 * country code are removed. (The API's own floor is `minLength: 10` on the
 * formatted string; ten real digits clears it.)
 */
export function isCheckoutPhone(value: string | null | undefined): boolean {
    if (!value) return false;
    let digits = value.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
    return digits.length === 10;
}
